import {
  MediaPlayer,
  MediaProvider,
  type MediaPlayerInstance,
  type MediaTimeUpdateEventDetail,
} from '@vidstack/react'
import {
  DefaultVideoLayout,
  defaultLayoutIcons,
} from '@vidstack/react/player/layouts/default'
import { useRef, useEffect, useCallback, useState } from 'react'
import {
  createPlaybackRecovery,
  type PlaybackRecovery,
  type PlaybackRecoveryState,
} from './playbackRecovery'
import '@vidstack/react/player/styles/default/theme.css'
import '@vidstack/react/player/styles/default/layouts/video.css'
import {
  VideoProgressTracker,
  type VideoMilestoneTrackingConfig,
} from './VideoProgressTracker'
import { LargePlayButton } from './LargePlayButton'
import { VideoChapters } from './VideoChapters'
import {
  createProgressCommitter,
  type ProgressCommitCallback,
  type ProgressCommitter,
} from './progressCommitter'
export { shouldRetryVideoProgressMutation } from './progressCommitter'

const PROGRESS_CHECKPOINT_INTERVAL_MS = 15_000
export const VIDEO_PROGRESS_MUTATION_SCOPE = { id: 'video-progress' } as const

/** Murphy's Magic streams from this bucket without CORS aligned to our origins; `crossOrigin="anonymous"` prevents metadata/load (legacy WebForms skipped it). */
function isMagicDownloadsS3Url(url: string): boolean {
  return url.startsWith('https://magicdownloads.s3.amazonaws.com')
}

function effectiveStartTime(
  lastAccessPoint: number,
  durationSeconds?: number | null
): number {
  if (lastAccessPoint <= 0) return 0
  if (!durationSeconds || durationSeconds <= 0) return lastAccessPoint
  return lastAccessPoint >= durationSeconds * 0.95 ? 0 : lastAccessPoint
}

export const VideoPlayer = (props: VideoPlayerProps) => (
  <VideoPlayerSession
    key={props.url ? (props.fileId ?? props.url) : 'empty'}
    {...props}
  />
)

const VideoPlayerSession = ({
  url,
  name,
  startTime,
  duration: durationSeconds,
  chaptersVtt,
  fileId,
  productId,
  milestoneTracking,
  onProgressCommit,
  className,
  originalUrl,
}: VideoPlayerProps) => {
  const playerRef = useRef<MediaPlayerInstance>(null)
  const onProgressCommitRef = useRef(onProgressCommit)
  const fileIdRef = useRef(fileId)
  const checkpointIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )
  const [start] = useState(() =>
    effectiveStartTime(startTime ?? 0, durationSeconds)
  )
  // The file identifies the session; refreshed signatures must not restart it.
  const [initialSources] = useState(() => ({
    source: url ?? '',
    originalSource: originalUrl,
  }))
  const [milestonesFired] = useState(() => new Set<string>())
  const latestCurrentTimeRef = useRef(start)
  const recoveryRef = useRef<PlaybackRecovery | null>(null)
  const sourceReadyRef = useRef(false)
  const wantsPlaybackRef = useRef(true)
  const mediaPreferencesRef = useRef({
    volume: 1,
    muted: false,
    playbackRate: 1,
  })
  const [mediaOptions, setMediaOptions] = useState({
    autoPlay: true,
    volume: 1,
    muted: false,
    playbackRate: 1,
  })
  const [playback, setPlayback] = useState<PlaybackRecoveryState>({
    phase: 'primary',
    source: initialSources.source,
    startTime: start,
    attempt: 0,
  })
  const isCurrentPlayback = useCallback(() => {
    const current = recoveryRef.current?.state
    return (
      current?.attempt === playback.attempt &&
      (current.phase === 'primary' || current.phase === 'original')
    )
  }, [playback.attempt])

  useEffect(() => {
    const recovery = createPlaybackRecovery({
      ...initialSources,
      startTime: start,
      readPosition: () => latestCurrentTimeRef.current,
      onChange: state => {
        sourceReadyRef.current = false
        setMediaOptions({
          autoPlay: wantsPlaybackRef.current,
          ...mediaPreferencesRef.current,
        })
        if (checkpointIntervalRef.current != null) {
          clearInterval(checkpointIntervalRef.current)
          checkpointIntervalRef.current = null
        }
        setPlayback(state)
      },
    })
    recoveryRef.current = recovery
    return () => {
      recovery.dispose()
      recoveryRef.current = null
    }
  }, [initialSources, start])

  useEffect(() => {
    recoveryRef.current?.updateSources(url ?? '', originalUrl)
  }, [url, originalUrl])

  const progressCommitterRef = useRef<ProgressCommitter | null>(null)

  useEffect(() => {
    if (progressCommitterRef.current != null) return

    const committer = createProgressCommitter((currentTime, currentFileId) =>
      onProgressCommitRef.current?.(currentTime, currentFileId)
    )
    committer.seed(start, fileId)
    progressCommitterRef.current = committer
  }, [fileId, start])

  useEffect(() => {
    onProgressCommitRef.current = onProgressCommit
    fileIdRef.current = fileId
  })

  const commitProgress = useCallback(() => {
    progressCommitterRef.current?.commit(
      latestCurrentTimeRef.current,
      fileIdRef.current
    )
  }, [])

  const handleTimeUpdate = useCallback(
    (detail: MediaTimeUpdateEventDetail) => {
      // Provider changes briefly report zero; don't overwrite the position being restored.
      if (
        isCurrentPlayback() &&
        sourceReadyRef.current &&
        Number.isFinite(detail.currentTime)
      ) {
        latestCurrentTimeRef.current = detail.currentTime
      }
    },
    [isCurrentPlayback]
  )

  const stopCheckpointing = useCallback(() => {
    if (checkpointIntervalRef.current == null) return

    clearInterval(checkpointIntervalRef.current)
    checkpointIntervalRef.current = null
  }, [])

  const startCheckpointing = useCallback(() => {
    stopCheckpointing()
    checkpointIntervalRef.current = setInterval(
      commitProgress,
      PROGRESS_CHECKPOINT_INTERVAL_MS
    )
  }, [commitProgress, stopCheckpointing])

  useEffect(() => {
    const handlePageHide = () => commitProgress()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        commitProgress()
      }
    }

    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopCheckpointing()
      commitProgress()
    }
  }, [commitProgress, stopCheckpointing])

  const handleCanPlay = useCallback(() => {
    if (sourceReadyRef.current || !isCurrentPlayback()) return
    sourceReadyRef.current = true
    const player = playerRef.current
    if (player && playback.startTime > 0)
      player.currentTime = playback.startTime
  }, [playback.startTime, isCurrentPlayback])

  const handleOnPause = () => {
    if (!isCurrentPlayback()) return
    wantsPlaybackRef.current = false
    stopCheckpointing()
    commitProgress()
  }

  const handleOnEnded = () => {
    if (!isCurrentPlayback()) return
    stopCheckpointing()
    commitProgress()
  }

  const wrapperClassName = [
    'relative mt-4 w-full aspect-video rounded-md bg-neutral-950',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handlePlaybackError = () => {
    if (!isCurrentPlayback()) return
    stopCheckpointing()
    recoveryRef.current?.error()
  }

  return (
    <>
      <div className={wrapperClassName}>
        {playback.phase === 'failed' ? (
          <div
            role="alert"
            className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center text-white"
          >
            <p>We couldn’t load this video. Please try again.</p>
            <button
              type="button"
              className="rounded-md bg-white px-4 py-2 font-semibold text-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
              onClick={() => {
                wantsPlaybackRef.current = true
                recoveryRef.current?.retry()
              }}
            >
              Try again
            </button>
          </div>
        ) : url ? (
          <MediaPlayer
            key={playback.attempt}
            className="h-full w-full text-white font-sans overflow-hidden rounded-md ring-media-focus data-[focus]:ring-4"
            title={name}
            src={playback.source}
            aspectRatio="16/9"
            playsInline={true}
            autoPlay={mediaOptions.autoPlay}
            ref={playerRef}
            crossOrigin={
              isMagicDownloadsS3Url(playback.source) ? undefined : 'anonymous'
            }
            volume={mediaOptions.volume}
            muted={mediaOptions.muted}
            playbackRate={mediaOptions.playbackRate}
            onVolumeChange={detail => {
              if (isCurrentPlayback())
                mediaPreferencesRef.current = {
                  ...mediaPreferencesRef.current,
                  ...detail,
                }
            }}
            onRateChange={playbackRate => {
              if (isCurrentPlayback())
                mediaPreferencesRef.current.playbackRate = playbackRate
            }}
            preload="auto"
            currentTime={playback.startTime}
            onCanPlay={handleCanPlay}
            onPlay={() => {
              if (!isCurrentPlayback()) return
              wantsPlaybackRef.current = true
              startCheckpointing()
            }}
            onError={handlePlaybackError}
            onHlsError={(detail: { fatal: boolean }) => {
              // Vidstack can recover fatal HLS errors internally without emitting onError.
              if (originalUrl && playback.phase === 'primary' && detail.fatal)
                handlePlaybackError()
            }}
            onPause={handleOnPause}
            onEnded={handleOnEnded}
            onTimeUpdate={handleTimeUpdate}
          >
            <MediaProvider>
              <VideoChapters chaptersVtt={chaptersVtt || undefined} />
            </MediaProvider>
            <DefaultVideoLayout icons={defaultLayoutIcons} />
            <LargePlayButton
              playerRef={playerRef as React.RefObject<MediaPlayerInstance>}
            />
            {fileId != null ? (
              <VideoProgressTracker
                playerRef={playerRef as React.RefObject<MediaPlayerInstance>}
                fileId={fileId}
                productId={productId}
                videoTitle={name}
                durationHint={durationSeconds}
                milestoneTracking={milestoneTracking}
                milestonesFired={milestonesFired}
              />
            ) : null}
          </MediaPlayer>
        ) : null}
      </div>
    </>
  )
}

export interface VideoPlayerProps {
  url?: string
  name?: string
  startTime?: number
  duration?: number | null
  chaptersVtt?: string | null
  fileId?: number
  /** When set (e.g. account downloads), Kissmetrics milestone events are recorded. */
  productId?: number
  /**
   * Custom milestone event config; takes precedence over `productId`. Use for
   * non-download surfaces such as VI Studio so the event name and KM property
   * labels match the surface.
   */
  milestoneTracking?: VideoMilestoneTrackingConfig
  onProgressCommit?: ProgressCommitCallback
  className?: string
  /** Signed original MP4 prepared alongside HLS after the initial access check. */
  originalUrl?: string | null
}
